/**
 * The app controller. It wires the SAME DOM-free core the terminal wires - auth → REST → WsSession →
 * Store - to a different render layer (workspace-client.md §5: the network and state layers are not
 * new, only the rendering is).
 *
 * The one rule this file is responsible for: EVERY intent below sends a canonical engine input through
 * the same authz as the terminal. There is no workspace-only verb here and there is nowhere for one to
 * hide, because the buttons that call these methods all come from `ui/affordances.ts`.
 */
import {
  AuthClient,
  DeliberateChoice,
  HttpClient,
  NoticeCodes,
  PROTOCOL_VERSION,
  ProtocolError,
  SessionClient,
  SettingsStore,
  Store,
  TokenManager,
  VersionClient,
  WsSession,
  buildJoinLink,
  decideUpdate,
  nameReadsAsCommand,
  parseJoinLink,
  rawWebSocketTransportFactory,
  withoutJoinLink,
} from '@core';
import { Outbox } from '@core';
import type {
  ApiError,
  GameActionPayload,
  NoticePayload,
  JoinIntent,
  ScenarioCard,
  SessionListItem,
  SessionView,
} from '@core';
import { moodAction } from '../ui/affordances.ts';
import { buddyAnswer, buddyFallback, readsAsCommand } from '../ui/buddy.ts';
import { setLanguage, t } from '../ui/i18n.ts';
import { ReadMarks, RoundLog, TEAM, buildWorkspace } from '../ui/model.ts';
import type { WorkspaceModel } from '../ui/model.ts';
import type { CastMember } from '../ui/cast.ts';
import { CLIENT_TYPE, resolveConfig } from './config.ts';
import type { AppConfig } from './config.ts';
import { browserKeyValueStore } from './browserEnv.ts';
import { WorkspaceUi } from '../ui/render/workspace.ts';

function errMsg(error: unknown): string {
  if (error instanceof ProtocolError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}

/**
 * The wait a rate-limited refusal implies (standards/messages.md §2.4: the action has to be TRUE).
 *
 * "Try again" is the right shape and the wrong advice while a limiter is holding the door - trying
 * again now fails again, and the player concludes the software is broken rather than that they are
 * early. The server already carries `retryAfterMs`; this turns it into the sentence.
 */
function waitHint(error: unknown): string | null {
  if (!(error instanceof ProtocolError) || error.retryAfterMs === null) {
    return null;
  }

  return t('app.problemWait', { seconds: Math.max(1, Math.ceil(error.retryAfterMs / 1000)) });
}

/** What the render layer is allowed to touch: read the model, call an intent. Never the network. */
export interface WorkspaceApi {
  model(): WorkspaceModel;
  playerId(): string | null;
  isAuthenticated(): boolean;
  isOwner(): boolean;
  connected(): boolean;
  isReadOnly(): boolean;
  /** True once this player is actually inside a room. The rail is not a way to tell: a signed-in
   * player with no room still has a buddy DM, and always will (§1a). */
  inWorkspace(): boolean;
  /** Rooms anyone can walk into, for the entry screen. */
  openWorkspaces(): SessionListItem[];
  /** Teams this player already belongs to - the entry screen's FIRST section (DEC-18). */
  myWorkspaces(): SessionListItem[];
  /**
   * The packaged decks a team can be started on (DEC-17 M4 increment 8b). Empty until the entry screen
   * has refreshed, which is what the create path falls back to its default for.
   */
  scenarios(): ScenarioCard[];
  /** The deck the picker currently has selected, or null before the catalog arrives. */
  chosenScenario(): string | null;
  /** Choose the deck the next "Start a new team" runs. */
  chooseScenario(ref: string): void;
  /** Problems worth showing, newest last. Each one names the next move (standards/messages.md). */
  notices(): string[];
  /** Marks a channel read up to its newest message — the badge's and the divider's other half. */
  markRead(channel: string, newest: number): void;

  signIn(displayName: string): Promise<void>;
  openWorkspace(id: string): Promise<void>;
  createWorkspace(name: string): Promise<void>;
  refreshOpenWorkspaces(): Promise<void>;
  say(text: string, channel?: string): Promise<void>;
  /** Say something to the buddy — answered client-side, scripted (§1a). */
  sayToBuddy(text: string): void;
  /** Ask a colleague something in their own DM, in your own words (§2c). */
  askColleague(seat: string, text: string): Promise<void>;
  /** Seats currently waiting on an answer, so the thread can show them typing rather than nothing. */
  askingSeats(): string[];
  /**
   * What this player has done that the server has not confirmed yet - the OUTBOX.
   *
   * Same shape as `askingSeats` above and for the same reason: an in-between state deserves to be on
   * screen rather than a pause with nothing in it. Saying something and reacting to something used to
   * wait a full round trip before appearing at all.
   */
  outbox(): Outbox;
  /** Send a refused line again. The text is still here, so nobody retypes their own sentence. */
  retry(cmdId: string): Promise<void>;
  /** Give up on a refused line and take it off the screen. */
  discard(cmdId: string): void;
  /** React to a committed message (§2i). Expressive, cited by NPC memory, never a meter. */
  react(seq: number, reaction: string): Promise<void>;
  /** Mark an outside-world message as READ (§2i). Idempotent server-side; costs the player nothing. */
  markSeen(stakeholder: string, seq: number): void;
  /** The reactions this client offers - a closed set, mirroring the server's own. */
  reactionChoices(): readonly string[];
  dispatch(input: GameActionPayload): Promise<void>;
  /** Sign off: the mood two-tap rides the same gesture as the check-in (§2m). */
  signOff(seat: string, mood: string | null): Promise<void>;
  shareLink(): string | null;
  leave(): Promise<void>;
}

const NOTICE_LIMIT = 4;

/**
 * The reactions this client offers, mirroring `ChatReactCommand.Allowed` (§2i). A closed set on both
 * sides: an open field would be a free-text channel that skipped every rule the free-text path has,
 * feeding a digest that is rendered into an NPC's prompt.
 */
const REACTIONS = ['👍', '👎', '✅', '🎉', '😡', '😕', '❤️', '👀'] as const;

export class WorkspaceApp implements WorkspaceApi {
  readonly store: Store;

  private readonly config: AppConfig;
  private readonly settings: SettingsStore;
  private readonly http: HttpClient;
  private readonly auth: AuthClient;
  private readonly tokens: TokenManager;
  private readonly sessions: SessionClient;

  /** Seats with a question in flight (§2j: the wait IS the character taking a moment). */
  private readonly asking = new Set<string>();

  /**
   * Outside-world messages already marked read, so the socket is not told the same thing every frame.
   *
   * Keyed by SESSION as well as stakeholder and seq. A player who leaves one workspace and joins
   * another without reloading keeps this cache, and stakeholder ids are content-level while seqs are
   * small integers per session - so `acme:3` in the second session is the same string as `acme:3` in
   * the first, and the mark it deserves would be swallowed as a duplicate. The trigger it feeds is
   * silence-shaped: nothing would look broken, the grievance simply would never arrive.
   */
  private readonly seen = new Set<string>();
  private readonly versions: VersionClient;
  private readonly ws: WsSession;
  private readonly ui: WorkspaceUi;
  private readonly rounds = new RoundLog();
  /**
   * How far this player has read in each channel (§2a, §2r). Client-local and staying that way: read
   * state that CHARACTERS can cite is §2i's `messageSeen` seam, which is not built, so nothing here
   * touches the wire and no NPC may reference it.
   */
  private readonly marks = new ReadMarks();

  private active: string | null = null;
  private ownerId: string | null = null;
  private publicList: SessionListItem[] = [];
  /** Teams this player belongs to (DEC-18). Includes private ones - the server scopes it by membership. */
  private myList: SessionListItem[] = [];
  /**
   * The unconfirmed half of what this player has done. NOT in the store, deliberately: the store is the
   * fold of committed events and is cached to IndexedDB for warm start, so an optimistic line written
   * there would come back after a reload as though it had been sent.
   */
  private readonly pending = new Outbox();

  /** The packaged decks a team can be started on (DEC-17 M4 increment 8b). */
  private deckList: ScenarioCard[] = [];
  /** The deck the picker has selected; null until the catalog arrives. */
  private chosenDeck: string | null = null;
  private noticeLines: string[] = [];

  /**
   * The "yes, really" for the name box: minting a second identity, or taking a name that reads as a
   * command. One gate for both, because somebody shown two reasons to stop who presses again anyway
   * has answered both of them.
   */
  private readonly newIdentity = new DeliberateChoice();
  private joinCode: string | null = null;
  /** True while the boot-time "do we already know this player?" probe is in flight (see resumeIdentity). */
  private probing = false;
  /** Lines the buddy has said back in the DM this session. Client-side and scripted (§1a). */
  /**
   * The buddy thread, BOTH SIDES of it.
   *
   * It used to hold her answers only, which meant your own half of the conversation was never
   * recorded anywhere: you typed, she replied, and your line simply did not exist. That is a
   * different bug from the outbox one - nothing here goes to the server at all (§1a's onboarding is
   * scripted and client-side, so it survives a dead provider), so there was no committed event coming
   * later to put it right. It was just missing.
   */
  private readonly buddyTurns: { mine: boolean; text: string }[] = [];

  /**
   * The questions this player has put to a colleague, in order.
   *
   * Client-local by construction: an ask is not a committed message, only its ANSWER comes back
   * (`npcReplies` is a per-player slice). So nothing was recording the question, and the thread showed
   * her answering nobody - the same shape as the buddy bug, on the other DM path.
   */
  private readonly askedLines: { seat: string; text: string }[] = [];

  constructor(
    mount: HTMLElement,
    language: string,
    private readonly invitation?: JoinIntent,
  ) {
    this.settings = new SettingsStore(browserKeyValueStore());
    this.config = resolveConfig(this.settings, language);
    setLanguage(this.config.language);
    this.store = new Store();

    this.http = new HttpClient({
      baseUrl: this.config.serverUrl,
      clientVersion: this.config.clientVersion,
      language: this.config.language,
      tokenProvider: () => this.tokens.getAccessToken(),
    });
    this.auth = new AuthClient(this.http);
    this.tokens = new TokenManager({
      authClient: this.auth,
      onAccessToken: (token) => this.ws.reauth(token),
      onRefreshError: () => {
        if (!this.probing) this.notice(t('app.problemSignedOut'));
      },
    });
    this.sessions = new SessionClient(this.http);
    this.versions = new VersionClient(this.http);
    this.ws = new WsSession({
      url: this.config.wsUrl,
      transportFactory: rawWebSocketTransportFactory,
      store: this.store,
      tokenProvider: () => this.tokens.getAccessToken(),
      clientVersion: this.config.clientVersion,
      platform: this.config.platform,
      language: this.config.language,
      onAuthError: (e) => void this.handleAuthError(e),
      onUpgradeRequired: () => this.reload(),
      // The targeted refusal (playtest one). The slot has been on the wire since the protocol mirror
      // was written and no client here read it, so a `sys.notice` the server sent this player landed
      // nowhere at all - which is the same silence the notice exists to end.
      onNotice: (n) => this.notice(this.noticeLine(n)),
    });

    this.ui = new WorkspaceUi(mount, this);
    this.store.subscribe(() => {
      // The round in force at the newest applied seq — the only way a committed message can be placed
      // on a workday later (ui/model.ts RoundLog explains why, and what it costs).
      const view = this.active ? this.store.getSession(this.active) : undefined;
      if (view?.game) {
        this.rounds.observe(view.seq, view.game.round);
      }

      // COALESCED, and this is a correctness fix rather than a performance one (§2f e2e, 2026-08-06).
      //
      // `render()` rebuilds the whole tree with `replaceChildren`, and every committed event notifies
      // the store. One player gesture fans out into a cascade of committed events - narration, NPC
      // speech, the watercooler, the milestone note, stakeholder reactions - so a single sign-off could
      // land 50+ full-tree rebuilds inside a second and a half. Every one of them replaces the DOM node
      // under the player's cursor.
      //
      // The consequence is not slowness, it is that BUTTONS STOP WORKING. A click needs its target to
      // hold still - a real user's mouse-down and mouse-up have to land on the same element, and
      // Playwright's actionability check states the same requirement as "a stable bounding box for two
      // consecutive frames". During the cascade that never happens, so the click waits, and waits.
      //
      // One render per frame is the fix, and one frame is the honest unit: it is the shortest interval
      // in which a person can perceive a change or complete a click. State-derived, not a latch - the
      // pending flag only says "a render is already scheduled", and every notification is still read.
      this.scheduleRender();
    });
  }

  /** True while a coalesced render is already queued for the next frame. */
  private renderPending = false;

  /**
   * Queues one render for the next animation frame, however many notifications arrive before it.
   *
   * `requestAnimationFrame` rather than a microtask: a microtask still runs many times per frame under
   * a burst, which is the same thrash with extra steps. The browser is already telling us when the
   * screen can change, and that is exactly the cadence a rebuild belongs on.
   */
  private scheduleRender(): void {
    if (this.renderPending) {
      return;
    }

    this.renderPending = true;
    const frame =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame
        : (cb: FrameRequestCallback) => setTimeout(() => cb(0), 16);

    frame(() => {
      this.renderPending = false;
      this.ui.render();
    });
  }

  start(): void {
    this.ui.render();
    void this.checkForUpdate();
    if (this.invitation) {
      void this.openFromLink(this.invitation);
    } else {
      // A returning player already has an identity in the refresh cookie; asking before minting is what
      // keeps them the same person to the room they were in yesterday.
      void this.resumeIdentity();
    }
  }

  // ---- reads ---------------------------------------------------------------------------------------

  /**
   * How YOU appear in the buddy thread. The same human the rest of the room sees, so your own line in
   * a DM is not rendered by a different rule from your line in a channel.
   */
  private selfSender(view: SessionView | undefined, playerId: string | null): CastMember {
    const name = view?.members.find((m) => m.playerId === playerId)?.displayName;
    return {
      id: `player:${playerId ?? 'me'}`,
      name: name || t('app.you'),
      avatar: null,
      kind: 'person' as const,
      subtitle: null,
    };
  }

  model(): WorkspaceModel {
    const view = this.active ? this.store.getSession(this.active) : undefined;
    const model = buildWorkspace(
      view,
      this.playerId(),
      this.rounds,
      this.marks,
      this.pending,
      this.askedLines,
    );
    if (this.buddyTurns.length === 0) {
      return model;
    }

    // This exchange is the one thing this client renders that the server did not send, and it is
    // declared as such: scripted onboarding lines, never engine state, never in the log. BOTH halves,
    // because a thread that shows only one person talking is not a thread.
    const dm = model.channels.find((c) => c.kind === 'dm');
    if (dm) {
      const day = model.day;
      const buddy = dm.messages[0]?.sender ?? {
        id: 'character:buddy',
        name: '',
        avatar: null,
        kind: 'person' as const,
        subtitle: null,
      };
      const me = this.playerId();

      dm.messages = [
        ...dm.messages,
        ...this.buddyTurns.map((turn, i) => ({
          id: `buddy-turn-${i}`,
          sender: turn.mine ? this.selfSender(view, me) : buddy,
          text: turn.text,
          note: null,
          day,
          clock: '',
          realTime: null,
          kind: 'buddy' as const,
          // Not a committed message, so nothing can react to it (§2i): a reaction points at a seq, and
          // these lines are the one thing this client renders that the server never sent.
          seq: null,
          reactions: [],
          actions: [],
          order: Number.MAX_SAFE_INTEGER - 100 + i,
        })),
      ];
    }

    return model;
  }

  playerId(): string | null {
    return this.tokens.getPlayerId() ?? this.store.getState().identity.playerId;
  }
  isAuthenticated(): boolean {
    return this.tokens.isAuthenticated();
  }
  isOwner(): boolean {
    const me = this.playerId();
    return Boolean(me && this.ownerId && me === this.ownerId);
  }
  connected(): boolean {
    return this.store.getConnection().status === 'live';
  }
  isReadOnly(): boolean {
    return this.store.isHydratedFromCache();
  }
  openWorkspaces(): SessionListItem[] {
    return this.publicList;
  }
  myWorkspaces(): SessionListItem[] {
    return this.myList;
  }
  scenarios(): ScenarioCard[] {
    return this.deckList;
  }
  chosenScenario(): string | null {
    return this.chosenDeck;
  }
  chooseScenario(ref: string): void {
    this.chosenDeck = ref;
    this.ui.render();
  }
  notices(): string[] {
    return this.noticeLines;
  }
  activeId(): string | null {
    return this.active;
  }
  inWorkspace(): boolean {
    return this.active !== null;
  }
  markRead(channel: string, newest: number): void {
    this.marks.read(channel, newest);
  }

  // ---- intents -------------------------------------------------------------------------------------

  async signIn(displayName: string): Promise<void> {
    const wanted = displayName.trim();
    try {
      if (wanted.length === 0) {
        this.newIdentity.clear();
        if (await this.resumeIdentity()) {
          return;
        }
      } else {
        // WARN, THEN GET OUT OF THE WAY (playtest one). The first real player left three guests behind
        // in one session, two of them named after whatever they had typed while working out how to
        // drive this. Both of these are still the player's call - a silly name is a name, and a second
        // identity is sometimes exactly what somebody wants - so it is a sentence and a second press.
        const warnings = await this.identityWarnings(wanted);
        if (warnings.length > 0 && !this.newIdentity.confirms(wanted)) {
          this.noticeLines = [];
          for (const warning of warnings) {
            this.notice(warning);
          }

          this.notice(t('app.identityConfirm'));
          return this.ui.render();
        }
      }

      this.newIdentity.clear();
      this.tokens.adopt(await this.auth.guest(wanted || undefined));
      // The problem is over, so its advice goes with it. A notice that outlives what it was about is
      // stale advice sitting under every later screen - and "wait 6s and go again", still on display
      // ten minutes after you got in, is worse than no line at all.
      this.noticeLines = [];
      if (this.ws.isLive()) {
        // A live socket still carries the OLD identity's token, and `start()` is a no-op while it is
        // live - so switching identity has to re-handshake or the new person keeps the old one's rooms.
        this.ws.reconnect();
      } else {
        this.ws.start();
      }

      await this.refreshOpenWorkspaces();
    } catch (error) {
      this.notice(t('app.problemSignIn', { error: errMsg(error) }));
      const wait = waitHint(error);
      if (wait) this.notice(wait);
    }
    this.ui.render();
  }

  /**
   * What is worth saying before a NEW identity is minted under this name. Empty when there is nothing.
   *
   * The second half resumes the identity the refresh cookie holds, and that side effect IS the point:
   * the person who meant to come back as themselves is now back as themselves, and the person who
   * meant to start over presses again.
   */
  private async identityWarnings(wanted: string): Promise<string[]> {
    const warnings: string[] = [];
    if (nameReadsAsCommand(wanted)) {
      warnings.push(t('app.nameLooksLikeCommand', { name: wanted }));
    }

    if (!this.tokens.isAuthenticated()) {
      await this.resumeIdentity();
    }

    if (this.tokens.getPlayerId() !== null) {
      warnings.push(t('app.identityExists', { teams: this.myList.length }));
    }

    return warnings;
  }

  /**
   * Takes back the identity the refresh cookie still holds, if it holds one.
   *
   * `probing` matters: a first-time visitor has NO cookie, the refresh 401s, and the token manager's
   * failure callback would announce "you have been signed out" to somebody who has never been signed
   * in - on the very first screen, above the name box, which is the worst possible first impression
   * and is also simply false. Asking whether an identity exists is not the same as losing one.
   */
  private async resumeIdentity(): Promise<boolean> {
    this.probing = true;
    try {
      await this.tokens.refreshNow();
    } catch {
      return false;
    } finally {
      this.probing = false;
    }

    if (this.tokens.getPlayerId() === null) {
      return false;
    }

    this.ws.start();
    await this.refreshOpenWorkspaces();
    this.ui.render();
    return true;
  }

  async refreshOpenWorkspaces(): Promise<void> {
    try {
      // Both, in one refresh (DEC-18). With sessions private by default the public directory is often
      // EMPTY, so a door that only browsed it would show a returning player nothing at all - including
      // the team they were in ten minutes ago.
      // The deck catalog rides the same refresh (DEC-17 M4 increment 8b). Before this the client
      // hard-coded `signal-triage@1` here, so four shipped decks could not be started from this door
      // at all - authored content no live path reached, which is the register's dead-content pattern.
      const [mine, open, decks] = await Promise.all([
        this.sessions.listMine().catch(() => ({ items: [] as SessionListItem[] })),
        this.sessions.listPublic(),
        this.sessions.listScenarios().catch(() => ({ scenarios: [] as ScenarioCard[] })),
      ]);
      this.myList = mine.items;
      this.publicList = open.items;
      this.deckList = decks.scenarios;

      // Preselect signal-triage when it is there, so pressing "Start a new team" without touching the
      // picker does exactly what it did before the picker existed.
      if (!this.chosenDeck) {
        this.chosenDeck =
          this.deckList.find((d) => d.id === 'signal-triage')?.ref ?? this.deckList[0]?.ref ?? null;
      }
    } catch {
      // Nothing for the player to do about an empty list they did not ask for; the entry screen already
      // offers the other door (start a team), so a line here would be noise, not a next move.
    }
    this.ui.render();
  }

  async createWorkspace(name: string): Promise<void> {
    if (!this.tokens.isAuthenticated()) {
      await this.signIn('');
    }
    try {
      // Always a scenario: a messenger with no game in it is a chat room, and this client exists to be
      // the game. `game` is what the terminal's `create <name> game` spells the same way.
      // PRIVATE (DEC-18). This client hard-coded `public`, which meant every team started in a
      // directory any stranger could walk in from and read the chat of - the privacy footgun the
      // decision is about, and it was worse here than on the terminal because this client creates a
      // team in one click with no visibility question anywhere in the flow.
      //
      // Nothing else in the flow changes: `adopt` already takes the join code the server returns for a
      // private session, and §1b's lobby already invites by link. Publishing to the directory becomes a
      // deliberate act rather than the accident of pressing "Start a new team".
      // Cleared before the call for the same reason the join path clears it there: the server may
      // answer with a notice, and a tidy-up that runs afterwards eats it.
      this.noticeLines = [];
      const detail = await this.sessions.create({
        name,
        visibility: 'private',
        // The picked deck, or the deck this client always ran when the catalog has not arrived. The
        // fallback is not a shrug: a create that failed because a list was slow would be a worse
        // outcome than starting the team everybody started before.
        scenarioRef: this.chosenDeck ?? 'signal-triage@1',
      });
      this.adopt(detail.id, detail.ownerId, detail.joinCode ?? null);
      this.store.seedMembers(detail.id, detail.members);
      this.ws.reconnect();
    } catch (error) {
      this.notice(t('app.problemCreate', { error: errMsg(error) }));
      const wait = waitHint(error);
      if (wait) this.notice(wait);
    }
    this.ui.render();
  }

  async openWorkspace(id: string, joinCode?: string): Promise<void> {
    if (!this.tokens.isAuthenticated()) {
      await this.signIn('');
    }
    try {
      // Cleared BEFORE the join, not after. Entering a room drops the last room's notices - but the
      // join itself now sends one back (DEC-19: which role you were given, or that you are watching),
      // and clearing afterwards was a race this client kept losing: the sentence arrived on the socket
      // and was wiped a millisecond later by the tidy-up for the room it was about.
      this.noticeLines = [];
      await this.sessions.join(id, joinCode);
      const detail = await this.sessions.get(id);
      this.store.seedMembers(id, detail.members);
      this.adopt(id, detail.ownerId, joinCode ?? null);
      this.ws.reconnect();
    } catch (error) {
      this.notice(t('app.problemJoin', { error: errMsg(error) }));
      const wait = waitHint(error);
      if (wait) this.notice(wait);
    }
    this.ui.render();
  }

  private adopt(id: string, ownerId: string | null, joinCode: string | null): void {
    this.active = id;
    this.ownerId = ownerId;
    this.joinCode = joinCode;
  }

  /**
   * Say something in a room (register item 16).
   *
   * `channel` is the room it was typed in, and it has to be carried because until item 16 there was
   * only ONE room on the wire: this client laid seven over it and the composer faithfully sent
   * everything into the only chat that existed, so a line typed in `#war-room` surfaced in `#team`.
   * A message that moves rooms is a fiction-breaker of the first order - it teaches players that the
   * rooms are painted on, which they were.
   *
   * Omitted for `#team` rather than sent explicitly, so the common case puts the same frame on the
   * wire it always did.
   */
  async say(text: string, channel?: string): Promise<void> {
    if (this.isReadOnly()) {
      return this.notice(t('app.reconnecting'));
    }
    if (!this.active) {
      return;
    }
    await this.post(this.active, channel === TEAM ? null : (channel ?? null), text);
  }

  /**
   * Say something in two stages (the outbox).
   *
   * Stage one is the line appearing NOW, marked as going out. Stage two is the committed event
   * arriving through the store exactly as it always did, at which point the pending copy stops being
   * drawn - `AckPayload.seq` is what correlates the two, so this needed no wire change.
   *
   * A refusal does not delete the line. It marks it, keeps the words, and offers to send them again;
   * a failure that eats what you typed makes you reconstruct your own sentence from memory, which is
   * a worse outcome than the one that caused it.
   */
  private async post(session: string, channel: string | null, text: string): Promise<void> {
    const cmdId = this.ws.newCmdId();
    this.pending.say(cmdId, session, channel, text);
    this.ui.render();

    try {
      const ack = await this.ws.sendChat(session, text, channel ?? undefined, cmdId);
      this.pending.accepted(cmdId, ack.seq ?? null);
    } catch (error) {
      this.pending.refused(cmdId, errMsg(error));
      const wait = waitHint(error);
      if (wait) {
        this.notice(wait);
      }
    }

    this.ui.render();
  }

  /** @inheritdoc */
  async retry(cmdId: string): Promise<void> {
    const line = this.pending.lineAt(cmdId);
    if (!line) {
      return;
    }

    // A NEW command id, not the one that was refused. The retry-the-same-id rule is about a command
    // that may already have landed; a refused one never committed, so there is nothing to deduplicate
    // against and reusing the id would only risk replaying the refusal.
    this.pending.forget(cmdId);
    await this.post(line.session, line.channel, line.text);
  }

  /** @inheritdoc */
  discard(cmdId: string): void {
    this.pending.forget(cmdId);
    this.ui.render();
  }

  /** @inheritdoc */
  outbox(): Outbox {
    return this.pending;
  }

  /**
   * The buddy's DM. Answered here rather than on the wire: §1a's lines are scripted so the first ninety
   * seconds survive a dead provider, and free text to real characters is step 4 (gated on the two C2
   * seams §4a names). What must never happen is silence, so there is no path through here that says
   * nothing.
   */
  sayToBuddy(text: string): void {
    const view = this.active ? this.store.getSession(this.active) : undefined;

    // YOUR line first, then hers. Nothing here goes to the server, so there is no round trip to be
    // optimistic about - this was simply never recorded, and the thread showed her answering nobody.
    this.buddyTurns.push({ mine: true, text });
    this.buddyTurns.push({
      mine: false,
      text: readsAsCommand(text) ? buddyAnswer(Boolean(view?.game?.activeEvent)) : buddyFallback(),
    });
    this.ui.render();
  }

  /**
   * Free text to a real colleague (§2c, build step 4).
   *
   * The question goes to the server, which compiles it to one of the closed topics the engine can
   * ground and answers through the existing NPC path; the reply and its evidence quote come back on
   * the per-player slice the DM renders. Nothing here parses the question.
   *
   * **A parser error must never surface in a DM.** §2c is explicit that one visible `unknown-topic:`
   * string kills the fiction for the session - and the server's own refusal is exactly that: it names
   * the topic KEYS ("they can talk about: mood, decision:comms-posture"), which is a correct API
   * message and a terrible thing for Daniel to say. So a refusal is rendered as the character
   * declining, in their voice, which is the scripted floor the degrade tier already guarantees.
   */
  async askColleague(seat: string, text: string): Promise<void> {
    if (!this.active || !text.trim()) {
      return;
    }
    if (this.isReadOnly()) {
      return this.notice(t('app.reconnecting'));
    }

    // YOUR question, on screen before hers (§2c). Recorded first so the thread shows what she is
    // thinking about while the "…" indicator runs, rather than a colleague pondering nothing.
    this.askedLines.push({ seat, text: text.trim() });

    // Latency is characterization here (§2j): a colleague who takes a moment reads as thinking, and
    // the alternative is a thread that sits empty while a model works.
    this.asking.add(seat);
    this.ui.render();
    try {
      const reply = await this.sessions.askNpc(this.active, seat, '', text.trim());
      this.store.applyNpcReply(this.active, reply);
    } catch (error) {
      // In character, and NOT via the notice bar: a refusal that appears as a system banner is the
      // interface admitting there is a parser behind the person. That rule is about what the PLAYER
      // sees, and it had grown to mean nobody hears about it either - a dead endpoint, a 500 and a
      // genuine deflection all rendered as the same calm line with nothing to tell them apart.
      //
      // But the two are NOT the same, and the server already distinguishes them. A question that
      // resolves to no topic comes back VALIDATION_FAILED: that is the closed-topic set working, the
      // designed answer, and gameplay rather than a fault. Anything else - a dead endpoint, a 500, a
      // rate limit - is a real failure and is the only thing worth an error. Logging both, which is
      // what the first version of this did, makes the log cry wolf every time somebody asks a
      // colleague something they cannot answer.
      if (!(error instanceof ProtocolError) || error.code !== 'VALIDATION_FAILED') {
        // eslint-disable-next-line no-console
        console.error('[workspace] colleague ask failed', { seat, error });
      }
      this.store.applyNpcReply(this.active, {
        seat,
        about: '',
        text: t('dm.deflect'),
        source: 'scripted',
        evidence: null,
      });
    } finally {
      this.asking.delete(seat);
      this.ui.render();
    }
  }

  /** @inheritdoc */
  askingSeats(): string[] {
    return [...this.asking];
  }

  /**
   * React to a message (§2i).
   *
   * Players trained by a decade of messengers WILL slap 😡 on Camila's post, and a medium that
   * silently discards that makes them feel unheard. It is committed so an NPC's memory fold can cite
   * it, and it moves no meter - the moment an emoji is worth morale it stops being expression and
   * becomes a button to farm.
   */
  async react(seq: number, reaction: string): Promise<void> {
    if (!this.active || this.isReadOnly()) {
      return;
    }
    // The same two stages, and simpler: a reaction is about a seq the client already knew, so there is
    // nothing to correlate afterwards - the ack IS the end of it, and the committed event folds into
    // the store's own reaction map.
    const cmdId = this.ws.newCmdId();
    this.pending.react(cmdId, this.active, seq, reaction);
    this.ui.render();

    try {
      await this.ws.sendReaction(this.active, seq, reaction, cmdId);
      this.pending.accepted(cmdId, null);
    } catch {
      // Still no banner: a failed reaction is the least consequential thing that can go wrong here,
      // and a notice would cost more attention than the gesture is worth. What changed is that the
      // tap no longer vanishes - it stays marked, because a gesture that silently did nothing teaches
      // a player that the button is decorative.
      this.pending.refused(cmdId, 'refused');
    }

    this.ui.render();
  }

  /**
   * Mark an outside-world message read (§2i).
   *
   * Deduped locally before it is sent, and idempotent on the server anyway - this fires on every
   * render of the open channel, which is many times per session per message. The engine treats a
   * re-mark as a silent no-op because re-marking is what a CORRECT client does; the local set just
   * keeps the socket quiet.
   *
   * Fire-and-forget by design. Being read costs the player nothing, so a failure here is worth no
   * banner and no retry - the next render sends it again.
   */
  markSeen(stakeholder: string, seq: number): void {
    if (!this.active || this.isReadOnly()) {
      return;
    }
    const mark = `${this.active}:${stakeholder}:${seq}`;
    if (this.seen.has(mark)) {
      return;
    }

    this.seen.add(mark);
    void this.ws
      .sendGameAction(this.active, {
        kind: 'markMessageSeen',
        stakeholder,
        messageRef: String(seq),
      })
      .catch(() => this.seen.delete(mark));
  }

  /** @inheritdoc */
  reactionChoices(): readonly string[] {
    return REACTIONS;
  }

  async dispatch(input: GameActionPayload): Promise<void> {
    if (this.isReadOnly()) {
      return this.notice(t('app.reconnecting'));
    }
    if (!this.active) {
      return;
    }
    try {
      const ack = await this.ws.sendGameAction(this.active, input);
      if (ack.status === 'rejected') {
        // The engine's refusals already name what is missing; the skin does not second-guess them, and
        // it does not translate them into fiction it cannot verify.
        this.notice(t('app.problemRefused', { message: ack.error?.message ?? '' }));
      }
    } catch (error) {
      this.notice(t('app.problemRefused', { message: errMsg(error) }));
    }
  }

  /**
   * One gesture, two signals (§2m). The mood rides the sign-off because game-loop §9.2 wants both and a
   * player should perform one ritual, not two chores; the mood goes FIRST so a rejected check-in does
   * not leave a mood recorded for a day that did not close.
   */
  async signOff(seat: string, mood: string | null): Promise<void> {
    if (mood) {
      // Through moodAction, which maps the button's word onto the engine's enum. Building the input
      // here by hand is what shipped `invalid Mood: 'good'` on every sign-off.
      await this.dispatch(moodAction(seat, mood));
    }

    await this.say(t('gesture.signOffLine'));
    await this.dispatch({ kind: 'checkIn', seat, mode: 'Passed' });
  }

  shareLink(): string | null {
    if (!this.active) {
      return null;
    }

    const loc = globalThis.location;
    const origin = loc ? `${loc.origin}${loc.pathname}` : '';
    return buildJoinLink(
      origin,
      this.joinCode
        ? { sessionId: this.active, joinCode: this.joinCode }
        : { sessionId: this.active },
    );
  }

  async leave(): Promise<void> {
    if (this.active) {
      try {
        await this.sessions.leave(this.active);
      } catch {
        // Leaving is local either way; a second attempt is not coming and saying so would be noise.
      }
    }
    this.active = null;
    this.ownerId = null;
    this.ws.reconnect();
    this.ui.render();
  }

  // ---- plumbing ------------------------------------------------------------------------------------

  private async openFromLink(intent: JoinIntent): Promise<void> {
    if (!this.tokens.isAuthenticated()) {
      await this.signIn('');
    }
    await this.openWorkspace(intent.sessionId, intent.joinCode);
    const cleaned = withoutJoinLink(globalThis.location?.href ?? '');
    globalThis.history?.replaceState?.(null, '', cleaned);
  }

  private async checkForUpdate(): Promise<void> {
    try {
      const decision = decideUpdate({
        clientVersion: this.config.clientVersion,
        clientProtocol: PROTOCOL_VERSION,
        manifest: await this.versions.manifest(),
        clientType: CLIENT_TYPE,
      });
      if (decision.action === 'force') {
        this.reload();
      }
    } catch {
      /* offline / manifest unavailable — non-fatal */
    }
  }

  private reload(): void {
    try {
      globalThis.location.reload();
    } catch {
      /* not a browser */
    }
  }

  private async handleAuthError(_error: ApiError): Promise<void> {
    try {
      await this.tokens.refreshNow();
      this.ws.reconnect();
    } catch {
      this.tokens.clear();
      this.notice(t('app.problemSignedOut'));
      this.ui.render();
    }
  }

  /**
   * One `sys.notice` as a workspace notice line.
   *
   * Written HERE rather than shown as the server sent it. The server's sentence is correct and names an
   * action, but it is written in the vocabulary the terminal shares with it - seats, `/claim` - and
   * §2g's law is that this workplace has people with jobs. So the CODE travels and the sentence is
   * local (messages.md §4), with the server's own text as the floor for a code this build never heard
   * of: an unrecognised notice still says something rather than nothing.
   */
  private noticeLine(n: NoticePayload): string {
    const roles = (n.seats ?? []).map((seat) => this.roleLabel(seat));
    switch (n.code) {
      case NoticeCodes.MentionNoSeat:
        if (roles.length === 0) return t('app.noticeNoRoleFull');
        return t('app.noticeNoRoleFree', { roles: roles.join(', ') });
      case NoticeCodes.MentionUnknownSeat:
        if (roles.length === 0) return t('app.noticeUnknownNameNobody');
        return t('app.noticeUnknownName', { names: roles.join(', ') });
      case NoticeCodes.MentionRateLimited:
        return t('app.noticeTooFast', {
          seconds: Math.max(1, Math.ceil((n.retryAfterMs ?? 1000) / 1000)),
        });
      case NoticeCodes.JoinSeated:
        // A confirmation, so it names no next move (messages.md §1) - and it names the PERSON whose
        // job you took over, because that is what the people list will show you a second later.
        return t('app.noticeSeated', { role: roles[0] ?? '' });
      case NoticeCodes.JoinObserver:
        return t('app.noticeObserver');
      default:
        return n.message ?? '';
    }
  }

  /**
   * A role key as the person who holds it - the same substitution the people rail makes. A notice that
   * said `comms` would be the one line on screen naming a colleague by their filename.
   */
  private roleLabel(seat: string): string {
    const member = this.model().members.find((m) => m.role === seat);
    return member?.who.name ?? seat;
  }

  private notice(message: string): void {
    this.noticeLines.push(message);
    if (this.noticeLines.length > NOTICE_LIMIT) {
      this.noticeLines = this.noticeLines.slice(-NOTICE_LIMIT);
    }
    this.ui.render();
  }
}

export { parseJoinLink };
